// Contract Register page — register of managed contracts with a plain
// Type label, status badges, search, status filter, performance grade
// column with sort + filter, a filter-aware summary strip, a
// Table↔Cards toggle, and a "New Contract" button. Links to detail and
// form pages.
//
// Data source: useContractRegister (full-drain envelope, view=register)
// fetches the complete in-view set across ALL statuses with no server
// filters, so the client-side strip + filters operate over the full set.
// Status, grade, and search are all narrowed client-side in
// `filteredContracts` and the URL persists filter/sort state.
//
// Summary strip: three StatBlocks recomputed client-side over the
// filtered set (contracts-in-view, total ex-GST value, active contracts
// expiring within 90 days of the real-clock today). It mirrors the
// backend contractRegisterSummary basis (totals over all statuses,
// active-only 90-day window). When the envelope reports complete=false
// the strip is suppressed — its totals would be misleading over a
// truncated set — and a refinement banner shows instead. At normal
// scale that branch never fires; it's a guardrail.
//
// The Grade column reads `contract.performanceGrade` populated by the
// list endpoint server-side (Option C — backend computes per request)
// so each row needs no extra client query. Filter chips group by tier
// (A, B, C, D, E, Awaiting data); sort cycles asc → desc → none with
// Awaiting-data items always sorted last so they don't crowd the top
// of either direction.

import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Plus,
  Search,
  FileText,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  LayoutGrid,
  Table as TableIcon,
} from "lucide-react";
import {
  Button,
  LoadingSpinner,
  Alert,
  Badge,
  StatBlock,
  Sparkline,
} from "../../components/common";
import { GradeLetter } from "../../components/manage/contract-overview/GradeLetter";
import { useContractRegister } from "../../hooks/useManagedContracts";
import { formatDayMonthYear } from "../../utils/formatShortDate";
import { formatAud0dp } from "../../utils/formatCurrency";
import { useAuth } from "../../contexts/AuthContext";
import type {
  ManagedContractStatus,
  ManagedContractType,
  ManagedContract,
  GradeLetterValue,
  ContractRegisterSummary,
} from "../../types";

// Compact labels for the status filter chip row. The "All" chip uses
// the empty-string sentinel that the data hook treats as "no filter".
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "terminated", label: "Terminated" },
  { value: "archived", label: "Archived" },
];

type GradeFilter = "all" | GradeLetterValue | "awaiting";
type SortDir = "asc" | "desc" | "none";

// Sortable columns. `grade` is special-cased (severity-ordered with
// Awaiting-data sentinel last); the others use natural comparators.
type SortKey = "title" | "supplier" | "grade" | "endDate" | "value";

interface SortState {
  key: SortKey | null;
  dir: SortDir;
}

const GRADE_FILTERS: { value: GradeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "D", label: "D" },
  { value: "E", label: "E" },
  { value: "awaiting", label: "Awaiting data" },
];

// Numeric severity for sort: A best (1), E worst (5). Awaiting data
// gets a sentinel that always sorts last regardless of direction.
const GRADE_SEVERITY: Record<GradeLetterValue, number> = {
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
};
const AWAITING_SEVERITY = Number.POSITIVE_INFINITY;

function severityFor(contract: ManagedContract): number {
  const grade = contract.performanceGrade?.grade;
  if (!grade) return AWAITING_SEVERITY;
  return GRADE_SEVERITY[grade];
}

const statusBadgeVariant: Record<
  ManagedContractStatus,
  {
    variant:
      | "draft"
      | "active"
      | "pending"
      | "expired"
      | "completed"
      | "archived";
    label: string;
  }
> = {
  draft: { variant: "draft", label: "Draft" },
  active: { variant: "active", label: "Active" },
  expired: { variant: "expired", label: "Expired" },
  terminated: { variant: "archived", label: "Terminated" },
  archived: { variant: "archived", label: "Archived" },
};

// Sortable column header — emits the brand "Sortable Column Header"
// pattern: ChevronsUpDown when inactive, ArrowUp/ArrowDown when active,
// `aria-sort` on the <th>, and a button child for keyboard access.
interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | null;
  currentDir: SortDir;
  onClick: () => void;
  align?: "left" | "right" | "center";
  testId?: string;
  ariaLabel?: string;
}

function SortableHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onClick,
  align = "left",
  testId,
  ariaLabel,
}: SortableHeaderProps) {
  const isActive = currentKey === sortKey && currentDir !== "none";
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? currentDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const thAlign =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";
  return (
    <th
      className={`${thAlign} px-4 py-3 text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider`}
      aria-sort={ariaSort}
    >
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        aria-label={ariaLabel ?? `Sort by ${label}`}
        className={`group inline-flex items-center ${align === "center" ? "justify-center" : ""} gap-1 text-caption font-medium uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-200 ${
          isActive
            ? "text-brand-600 dark:text-brand-400"
            : "text-slate-500 dark:text-slate-400"
        }`}
      >
        {label}
        {!isActive && (
          <ChevronsUpDown
            className="h-3.5 w-3.5 opacity-25 group-hover:opacity-60"
            aria-hidden="true"
          />
        )}
        {isActive && currentDir === "asc" && (
          <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {isActive && currentDir === "desc" && (
          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    </th>
  );
}

// Plain monochrome type text. Colour is reserved for Status/Grade — the
// type answers "what kind?" and never competes for the high-signal slot,
// so it carries no tint or pill (no violet, no amber).
function typeLabelText(type: ManagedContractType): string {
  return type === "contract" ? "Contract" : "Arrangement";
}

type ViewMode = "table" | "cards";
const VIEW_STORAGE_KEY = "cg-reg-view";

function readStoredView(): ViewMode {
  if (typeof window === "undefined") return "table";
  return window.localStorage.getItem(VIEW_STORAGE_KEY) === "cards"
    ? "cards"
    : "table";
}

// Whole-day difference between two YYYY-MM-DD calendar days, anchored at
// UTC midnight so DST/offset never shifts the day count. Returns null when
// either date is missing/invalid. Mirrors the backend diffDays semantics
// used by contractRegisterSummary (endDate - today).
function diffCalendarDays(from: string, to: string | undefined): number | null {
  if (!to) return null;
  const a = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

const EXPIRY_HORIZON_DAYS = 90;

// Recompute the strip over the already-filtered set so it reacts to
// status/grade/search. `today` is the real-clock UTC calendar day.
// Mirrors the backend contractRegisterSummary basis: totalValue spans all
// statuses in view; expiring is active-only within today..today+90d.
function computeSummary(
  items: ManagedContract[],
  today: string,
): ContractRegisterSummary {
  const contractCount = items.length;
  // Prefer the derived current total, falling back to the stored head value
  // (not 0) when a row lacks currentValue — a more useful in-view total than
  // the backend summary's `currentValue ?? 0`. Intentional divergence; the
  // backend can assume currentValue is always enriched, the client cannot.
  const totalValue = items.reduce(
    (sum, c) => sum + (c.currentValue ?? c.value),
    0,
  );

  // Active contracts ending within today..today+90d (inclusive). Capture the
  // validated endDate so downstream code carries a non-nullable string —
  // diffCalendarDays already returns null for missing/invalid dates.
  const expiringItems = items.flatMap((c) => {
    if (c.status !== "active") return [];
    const delta = diffCalendarDays(today, c.endDate);
    if (delta === null || delta < 0 || delta > EXPIRY_HORIZON_DAYS) return [];
    return [{ endDate: c.endDate as string, title: c.title }];
  });

  if (expiringItems.length === 0) {
    return { contractCount, totalValue, expiring: null };
  }

  const soonest = expiringItems.reduce((best, c) =>
    c.endDate < best.endDate ? c : best,
  );

  return {
    contractCount,
    totalValue,
    expiring: {
      count: expiringItems.length,
      soonestDate: soonest.endDate,
      soonestTitle: soonest.title,
    },
  };
}

function isGradeFilter(value: string | null): value is GradeFilter {
  return (
    value === "all" ||
    value === "A" ||
    value === "B" ||
    value === "C" ||
    value === "D" ||
    value === "E" ||
    value === "awaiting"
  );
}

function isSortKey(value: string | null): value is SortKey {
  return (
    value === "title" ||
    value === "supplier" ||
    value === "grade" ||
    value === "endDate" ||
    value === "value"
  );
}

// Per-column comparator. Returns negative when `a` should sort before
// `b` ascending. Awaiting-data grade is a special case handled in the
// grade comparator — surfaces as the AWAITING_SEVERITY sentinel.
function compareByKey(
  key: SortKey,
  a: ManagedContract,
  b: ManagedContract,
): number {
  switch (key) {
    case "title":
      return a.title.localeCompare(b.title, "en-AU", { sensitivity: "base" });
    case "supplier":
      // Sparse rows may lack supplierName — compare as the empty string so
      // they group at the top ascending instead of throwing.
      return (a.supplierName ?? "").localeCompare(b.supplierName ?? "", "en-AU", {
        sensitivity: "base",
      });
    case "grade":
      return severityFor(a) - severityFor(b);
    case "endDate": {
      // A missing endDate would yield NaN and make the sort order
      // unspecified — undated rows pin to +Infinity (last ascending).
      const ta = a.endDate ? new Date(a.endDate).getTime() : Number.POSITIVE_INFINITY;
      const tb = b.endDate ? new Date(b.endDate).getTime() : Number.POSITIVE_INFINITY;
      if (ta === tb) return 0; // also covers two undated rows (∞ - ∞ is NaN)
      return ta - tb;
    }
    case "value":
      // Sort by the derived current total when present so the column
      // sort agrees with what the user is seeing in the cell. Falls
      // back to the stored initial value for safety.
      return (a.currentValue ?? a.value) - (b.currentValue ?? b.value);
  }
}

export default function ContractRegister() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialise from ?search= so palette overflow rows ("Show all N…") pre-populate the field.
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [statusFilter, setStatusFilter] = useState("");

  const gradeParam = searchParams.get("grade");
  const gradeFilter: GradeFilter = isGradeFilter(gradeParam) ? gradeParam : "all";

  const sortParam = searchParams.get("sort");
  const dirParam = searchParams.get("dir");
  const sort: SortState = isSortKey(sortParam)
    ? {
        key: sortParam,
        dir: dirParam === "asc" || dirParam === "desc" ? dirParam : "none",
      }
    : { key: null, dir: "none" };

  const setGradeFilter = (next: GradeFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("grade");
    else params.set("grade", next);
    setSearchParams(params, { replace: true });
  };

  // Cycle a column through asc → desc → none. Distinct keys reset
  // each other so only one column is sorted at a time (multi-column
  // sort is overkill for this register's scale).
  const cycleSort = (key: SortKey) => {
    const params = new URLSearchParams(searchParams);
    if (sort.key !== key) {
      params.set("sort", key);
      params.set("dir", "asc");
    } else if (sort.dir === "asc") {
      params.set("sort", key);
      params.set("dir", "desc");
    } else {
      params.delete("sort");
      params.delete("dir");
    }
    setSearchParams(params, { replace: true });
  };

  const cycleGradeSort = () => cycleSort("grade");

  const [view, setView] = useState<ViewMode>(readStoredView);
  const changeView = (next: ViewMode) => {
    setView(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    }
  };

  // Full-drain register envelope: no server filters, so the client-side
  // strip + filters operate over the complete in-view set across all
  // statuses. Status is now narrowed client-side alongside search + grade.
  const { data, isLoading, error } = useContractRegister();

  const isComplete = data?.complete !== false;

  const canCreate =
    user?.role === "admin" ||
    user?.role === "editor" ||
    user?.role === "global-admin";

  const filteredContracts = useMemo(() => {
    let items = data?.items ?? [];
    if (statusFilter) {
      items = items.filter((c) => c.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      // contractNumber/supplierName are nullable in practice (stored rows can
      // predate fields the type declares required) — guard so one sparse row
      // can't take the register down.
      items = items.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          (c.contractNumber ?? "").toLowerCase().includes(q) ||
          (c.supplierName ?? "").toLowerCase().includes(q),
      );
    }
    if (gradeFilter !== "all") {
      items = items.filter((c) => {
        const g = c.performanceGrade?.grade ?? null;
        if (gradeFilter === "awaiting") return g === null;
        return g === gradeFilter;
      });
    }
    if (sort.key && sort.dir !== "none") {
      const direction = sort.dir === "asc" ? 1 : -1;
      const sortKey = sort.key;
      items = items.slice().sort((a, b) => {
        // Grade column anchors Awaiting-data records at the bottom in
        // both directions so the top of the list always shows graded
        // contracts. Other columns sort straight without the sentinel.
        if (sortKey === "grade") {
          const sa = severityFor(a);
          const sb = severityFor(b);
          if (sa === AWAITING_SEVERITY && sb !== AWAITING_SEVERITY) return 1;
          if (sb === AWAITING_SEVERITY && sa !== AWAITING_SEVERITY) return -1;
        }
        return compareByKey(sortKey, a, b) * direction;
      });
    }
    return items;
  }, [data?.items, statusFilter, search, gradeFilter, sort.key, sort.dir]);

  // Strip recomputes over the filtered set. Pin `today` to the real-clock
  // UTC calendar day once per render so the 90-day window is stable across
  // the three StatBlocks. Only meaningful when the list is complete.
  const summary = useMemo(
    () =>
      computeSummary(filteredContracts, new Date().toISOString().slice(0, 10)),
    [filteredContracts],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 text-slate-900 dark:text-slate-50">
            Contract Register
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Manage and track your organisation&apos;s contracts.
          </p>
        </div>
        {canCreate && (
          <Link to="/manage/contracts/new">
            <Button icon={<Plus className="h-4 w-4" />}>New Contract</Button>
          </Link>
        )}
      </div>

      {isComplete ? (
        <div
          data-testid="contract-summary-strip"
          className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        >
          <StatBlock
            label="Contracts in view"
            value={summary.contractCount}
            sub="matching current filters"
          />
          <StatBlock
            label="Total value"
            value={formatAud0dp(summary.totalValue)}
            sub="ex-GST, across all statuses in view"
          />
          <StatBlock
            label="Expiring ≤ 90 days"
            value={
              <span data-testid="summary-expiring">
                {summary.expiring?.count ?? 0}
                <span className="sr-only">
                  {summary.expiring
                    ? ` — soonest ${summary.expiring.soonestTitle} on ${formatDayMonthYear(summary.expiring.soonestDate)}`
                    : " — None"}
                </span>
              </span>
            }
            sub={
              summary.expiring
                ? `${summary.expiring.soonestTitle} · ${formatDayMonthYear(summary.expiring.soonestDate)}`
                : "None"
            }
          />
        </div>
      ) : (
        <div
          data-testid="register-incomplete-banner"
          className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-body-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
        >
          Showing the first 1000+ contracts — refine filters to see complete
          totals.
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by title, number, or supplier..."
            aria-label="Search contracts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9"
          />
        </div>
        <div
          role="group"
          aria-label="Choose layout"
          className="inline-flex rounded-md border border-slate-200 bg-slate-100 p-0.5 dark:border-slate-700 dark:bg-slate-800"
        >
          {(
            [
              { value: "table" as const, label: "Table", Icon: TableIcon },
              { value: "cards" as const, label: "Cards", Icon: LayoutGrid },
            ]
          ).map(({ value, label, Icon }) => {
            const active = view === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => changeView(value)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1 text-caption font-medium transition-colors ${
                  active
                    ? "bg-white text-slate-900 shadow-soft-sm dark:bg-slate-700 dark:text-slate-50"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="status-filter-chips"
        role="group"
        aria-label="Filter by status"
      >
        <span className="text-caption text-slate-500 dark:text-slate-400">
          Status:
        </span>
        {STATUS_FILTERS.map((opt) => {
          const active = statusFilter === opt.value;
          return (
            <button
              key={opt.value || "all"}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              data-testid={`status-filter-${opt.value || "all"}`}
              aria-pressed={active}
              className={`rounded-md px-2.5 py-1 text-caption font-medium transition-colors ${
                active
                  ? "bg-brand-600 text-white dark:bg-brand-500"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="grade-filter-chips"
        role="group"
        aria-label="Filter by performance grade"
      >
        <span className="text-caption text-slate-500 dark:text-slate-400">
          Grade:
        </span>
        {GRADE_FILTERS.map((opt) => {
          const active = gradeFilter === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setGradeFilter(opt.value)}
              data-testid={`grade-filter-${opt.value}`}
              aria-pressed={active}
              className={`rounded-md px-2.5 py-1 text-caption font-medium transition-colors ${
                active
                  ? "bg-brand-600 text-white dark:bg-brand-500"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {error && <Alert variant="error">{(error as Error).message}</Alert>}

      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      )}

      {!isLoading && filteredContracts.length === 0 && (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mb-4" />
          {(() => {
            const hasActiveFilters =
              !!search || !!statusFilter || gradeFilter !== "all";
            const rawListEmpty = (data?.items?.length ?? 0) === 0;
            // When filters are active, the filtered-empty case dominates —
            // the user can clear filters to see whatever is assigned.
            if (hasActiveFilters) {
              return (
                <p className="text-body text-slate-500 dark:text-slate-400">
                  No contracts match your filters.
                </p>
              );
            }
            // Differentiated copy when a scoped user genuinely has no
            // grants — direct them to ask an admin or contract owner
            // rather than leaving the empty state silent.
            if (rawListEmpty && user?.contractAccess === "selected") {
              return (
                <p className="text-body text-slate-500 dark:text-slate-400">
                  No contracts assigned. Ask an admin or contract owner to
                  grant you access.
                </p>
              );
            }
            return (
              <p className="text-body text-slate-500 dark:text-slate-400">
                No contracts found.
              </p>
            );
          })()}
        </div>
      )}

      {!isLoading && filteredContracts.length > 0 && view === "table" && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Number
                  </th>
                  <SortableHeader
                    label="Title"
                    sortKey="title"
                    currentKey={sort.key}
                    currentDir={sort.dir}
                    onClick={() => cycleSort("title")}
                    testId="title-sort-toggle"
                  />
                  <SortableHeader
                    label="Supplier"
                    sortKey="supplier"
                    currentKey={sort.key}
                    currentDir={sort.dir}
                    onClick={() => cycleSort("supplier")}
                    testId="supplier-sort-toggle"
                  />
                  <th className="text-left px-4 py-3 text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <SortableHeader
                    label="Grade"
                    sortKey="grade"
                    currentKey={sort.key}
                    currentDir={sort.dir}
                    onClick={cycleGradeSort}
                    align="center"
                    testId="grade-sort-toggle"
                  />
                  <SortableHeader
                    label="End Date"
                    sortKey="endDate"
                    currentKey={sort.key}
                    currentDir={sort.dir}
                    onClick={() => cycleSort("endDate")}
                    testId="end-date-sort-toggle"
                  />
                  <SortableHeader
                    label="Total value (ex-GST)"
                    sortKey="value"
                    currentKey={sort.key}
                    currentDir={sort.dir}
                    onClick={() => cycleSort("value")}
                    align="right"
                    testId="value-sort-toggle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredContracts.map((contract) => {
                  const badge = statusBadgeVariant[contract.status];
                  const grade = contract.performanceGrade ?? {
                    grade: null,
                    score: null,
                    label: "Awaiting data",
                    reportingPillarCount: 0,
                  };
                  return (
                    <tr
                      key={contract.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      data-testid={`contract-row-${contract.id}`}
                    >
                      <td className="px-4 py-3 text-body-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {typeLabelText(contract.type)}
                      </td>
                      <td className="px-4 py-3 font-mono text-body-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {contract.contractNumber}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/manage/contracts/${contract.id}`}
                          className="font-medium text-slate-900 dark:text-slate-50 hover:text-brand-600 dark:hover:text-brand-400"
                        >
                          {contract.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-body-sm text-slate-600 dark:text-slate-400">
                        {contract.supplierName}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={badge.variant} label={badge.label} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <GradeLetter result={grade} variant="compact" />
                      </td>
                      <td className="px-4 py-3 text-body-sm text-slate-600 dark:text-slate-400 whitespace-nowrap tabular-nums">
                        {formatDayMonthYear(contract.endDate)}
                      </td>
                      <td className="px-4 py-3 text-body-sm text-slate-600 dark:text-slate-400 text-right whitespace-nowrap tabular-nums">
                        {formatAud0dp(contract.currentValue ?? contract.value)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isLoading && filteredContracts.length > 0 && view === "cards" && (
        <ContractCards contracts={filteredContracts} />
      )}
    </div>
  );
}

// Value trend: the decorative (aria-hidden) sparkline always travels with
// an adjacent value text counterpart carrying the latest period value, so
// the trend is never conveyed by the chart alone.
// The card already shows the committed value as a heading, so the sparkline's
// paired value is sr-only — the trend stays non-decorative for screen readers
// (matching the supplier card's TrendCell) without printing the dollar amount
// twice, inches apart.
function ContractTrend({ series, value }: { series: number[]; value: number }) {
  return (
    <span
      className="inline-flex items-center"
      data-testid="contract-trend-sparkline"
    >
      <Sparkline
        data={series}
        className="text-brand-500 dark:text-brand-400"
      />
      <span className="sr-only">
        Value progression; current committed value {formatAud0dp(value)}
      </span>
    </span>
  );
}

function ContractCards({ contracts }: { contracts: ManagedContract[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {contracts.map((contract) => {
        const badge = statusBadgeVariant[contract.status];
        const grade = contract.performanceGrade ?? {
          grade: null,
          score: null,
          label: "Awaiting data",
          reportingPillarCount: 0,
        };
        const currentValue = contract.currentValue ?? contract.value;
        const trend = contract.valueTrend ?? [];
        return (
          <div
            key={contract.id}
            className="card flex flex-col gap-3 p-5"
            data-testid="contract-card"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="text-caption text-slate-500 dark:text-slate-400">
                  {typeLabelText(contract.type)}
                </span>
                <Link
                  to={`/manage/contracts/${contract.id}`}
                  className="block text-h4 text-slate-900 dark:text-slate-50 hover:text-brand-600 dark:hover:text-brand-400"
                >
                  {contract.title}
                </Link>
                <div className="text-caption text-slate-500 dark:text-slate-400">
                  {contract.contractNumber}
                  {contract.supplierName ? ` · ${contract.supplierName}` : ""}
                </div>
              </div>
              <Badge variant={badge.variant} label={badge.label} />
            </div>

            <div className="flex items-end justify-between gap-2">
              <div className="min-w-0">
                <p className="text-overline uppercase text-slate-500 dark:text-slate-400">
                  Total value
                </p>
                <p className="text-h3 tabular-nums text-slate-900 dark:text-slate-50">
                  {formatAud0dp(currentValue)}{" "}
                  <span className="text-caption font-normal text-slate-500 dark:text-slate-400">
                    ex GST
                  </span>
                </p>
              </div>
              {trend.length > 0 && (
                <ContractTrend series={trend} value={currentValue} />
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3 dark:border-slate-700">
              <div className="text-body-sm text-slate-600 dark:text-slate-400">
                {contract.endDate ? (
                  <>Ends {formatDayMonthYear(contract.endDate)}</>
                ) : (
                  "No end date"
                )}
              </div>
              <GradeLetter result={grade} variant="compact" />
            </div>
          </div>
        );
      })}
    </div>
  );
}
